import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  TrendingUp,
  DollarSign,
  Eye,
  Heart,
  MessageCircle,
  BarChart3,
  PieChart,
  Tag,
  GitCompare,
  Building2,
  Zap,
  Clock,
  Target,
  Users,
  Lock,
  RefreshCw,
  Info,
  AlertTriangle,
  LayoutGrid
} from 'lucide-react'
import ContentPerformancePage from '../content-performance/ContentPerformancePage'
import BrandValuePanel from '../content-performance/BrandValuePanel'
import NewFeaturesAnnouncement from './NewFeaturesAnnouncement'
import SharedReportsBanner from '../dashboard/SharedReportsBanner'
import { Card, DateRangePicker, Button, Tooltip } from '../ui'
import PostsTable from './PostsTable'
import EMVCharts from './EMVCharts'
// import HashtagAnalyticsPage from './HashtagAnalyticsPage'
import CompetitiveAnalysis from './CompetitiveAnalysis'
// Brand Discovery hidden again — NOT because it is broken. The two reader bugs that hid it
// are fixed (ath-atlas-be 58d7f81) and the read path is verified end to end. It is hidden
// because the only analyzed video in production is from Apr–May 2026: the media-analysis
// pipeline has produced nothing since 2026-05-04, so a customer opening this tab with a
// recent date range would see an empty feature. Re-enable once the pipeline is producing
// again (Google path, or the Claude frame-sampling pipeline) — see avi-backlog #27.
// import BrandAnalytics from './BrandAnalytics'
import EMVByPlatformCharts from './EMVByPlatformCharts'
import useSocialMediaStore from '../../stores/socialMediaStore'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import { formatEMV } from '../../utils/emvCalculator'
import {
  socialMediaService,
  DashboardAnalyticsParams
} from '../../services/socialMediaService'
import { useAuth } from '../auth/AuthProvider'
import usePermissionsStore from '../../stores/permissionsStore'
import useInstagramConnectionStore from '../../stores/instagramConnectionStore'
import { isRegularUser } from '../../utils/permissions'
import { isInstagramGraphApiEnabled } from '../../utils/featureFlags'

const SocialMediaDashboard: React.FC = () => {
  const {
    selectedCustomer,
    selectedAccount,
    getAPIFilterParams,
    loadingCustomers
  } = useGlobalCustomerStore()
  const { user } = useAuth()
  const { loadPermissions, hasFeature, permissions } = usePermissionsStore()
  const { connections, loadConnections } = useInstagramConnectionStore()

  const {
    posts,
    analytics,
    profiles,
    selectedProfile,
    filterByPlatform,
    calculateAnalytics,
    loading,
    error,
    // Dashboard API state
    currentCustomerId,
    dashboardAnalytics,
    currentPeriod,
    availablePlatforms,
    availablePostTypes,
    customDateRange,
    selectedPlatformIds,
    selectedPostType,
    // Dashboard API actions
    setCustomerId,
    setPeriod,
    setSelectedPlatformIds,
    setSelectedAccountIds,
    setCustomDateRange,
    setAccountType,
    loadAvailablePlatforms,
    loadAvailablePostTypes,
    loadDashboardAnalytics,
    loadTopPosts,
    filterByPostType,
    reset,
    accountType
  } = useSocialMediaStore()

  const [activeView, setActiveView] = useState<
    | 'overview'
    | 'posts'
    | 'hashtag-campaigns'
    | 'analytics-charts'
    | 'ai-intelligence'
    | 'profiles'
    | 'competitive-analysis'
    | 'brand-analytics'
    | 'content-performance'
    | 'brand-value'
  >('overview')
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all')
  const [localSelectedPostType, setLocalSelectedPostType] =
    useState<string>('all')
  const [emvTrendData, setEmvTrendData] = useState<
    Array<{ date: string; value: number }>
  >([])
  const [emvTrendLoading, setEmvTrendLoading] = useState(false)
  const [heatmapData, setHeatmapData] = useState<any>(null)
  const [heatmapLoading, setHeatmapLoading] = useState(false)
  const [emvByPlatformData, setEmvByPlatformData] = useState<any[]>([])
  const [emvByPlatformLoading, setEmvByPlatformLoading] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Enhanced API token expiry alerts are now surfaced app-wide via EnhancedApiTokenBanner (Layout).

  const handleRefresh = async () => {
    if (!currentCustomerId || isRefreshing) return
    setIsRefreshing(true)
    try {
      await Promise.all([loadDashboardAnalytics(), loadTopPosts()])
    } finally {
      setIsRefreshing(false)
    }
  }

  // Load EMV trend data for hero card
  useEffect(() => {
    const loadEmvTrend = async () => {
      if (!currentCustomerId) return

      setEmvTrendLoading(true)
      try {
        const globalFilterParams = getAPIFilterParams()
        const hasGlobalSelection = Object.keys(globalFilterParams).length > 0

        const params: DashboardAnalyticsParams = {
          customerId: currentCustomerId,
          period: currentPeriod,
          ...(hasGlobalSelection ? globalFilterParams : {})
        }

        if (currentPeriod === 'custom' && customDateRange) {
          params.customStartDate = customDateRange.start
          params.customEndDate = customDateRange.end
        }

        const response = await socialMediaService.getEmvOverTime(params)

        if (response.success && response.data) {
          // Transform API data to chart format
          const chartData = response.data.dailyData.map((day) => ({
            date: day.date,
            value: day.totalEmvValue
          }))
          setEmvTrendData(chartData)
        } else {
          setEmvTrendData([])
        }
      } catch (error) {
        console.error('Failed to load EMV trend data:', error)
        setEmvTrendData([])
      } finally {
        setEmvTrendLoading(false)
      }
    }

    loadEmvTrend()
  }, [
    currentCustomerId,
    currentPeriod,
    customDateRange,
    selectedAccount?.account?.id,
    selectedAccount?.type
  ])

  // Load heatmap data for posting activity
  useEffect(() => {
    const loadHeatmap = async () => {
      if (!currentCustomerId) return

      setHeatmapLoading(true)
      try {
        const globalFilterParams = getAPIFilterParams()
        const hasGlobalSelection = Object.keys(globalFilterParams).length > 0

        const params: DashboardAnalyticsParams = {
          customerId: currentCustomerId,
          period: currentPeriod,
          ...(hasGlobalSelection ? globalFilterParams : {})
        }

        if (currentPeriod === 'custom' && customDateRange) {
          params.customStartDate = customDateRange.start
          params.customEndDate = customDateRange.end
        }

        const response = await socialMediaService.getPostHeatmap(params)

        if (response.success && response.data) {
          setHeatmapData(response.data.postingActivity)
        } else {
          setHeatmapData(null)
        }
      } catch (error) {
        console.error('Failed to load heatmap data:', error)
        setHeatmapData(null)
      } finally {
        setHeatmapLoading(false)
      }
    }

    loadHeatmap()
  }, [
    currentCustomerId,
    currentPeriod,
    customDateRange,
    selectedAccount?.account?.id,
    selectedAccount?.type
  ])

  // Load EMV by platform data for stacked charts
  useEffect(() => {
    const loadEmvByPlatform = async () => {
      if (!currentCustomerId) return

      setEmvByPlatformLoading(true)
      try {
        const globalFilterParams = getAPIFilterParams()
        const hasGlobalSelection = Object.keys(globalFilterParams).length > 0

        const params: DashboardAnalyticsParams = {
          customerId: currentCustomerId,
          period: currentPeriod,
          ...(hasGlobalSelection ? globalFilterParams : {})
        }

        // Add platform filter from store
        if (selectedPlatformIds.length > 0) {
          params.platformIds = selectedPlatformIds
        }

        // Add post type filter from store
        if (selectedPostType && selectedPostType !== 'all') {
          params.postTypes = [selectedPostType]
        }

        if (currentPeriod === 'custom' && customDateRange) {
          params.customStartDate = customDateRange.start
          params.customEndDate = customDateRange.end
        }

        const response = await socialMediaService.getEmvByDatePlatform(params)

        if (response.success && response.data) {
          setEmvByPlatformData(response.data.dailyPlatformData)
        } else {
          setEmvByPlatformData([])
        }
      } catch (error) {
        console.error('Failed to load EMV by platform data:', error)
        setEmvByPlatformData([])
      } finally {
        setEmvByPlatformLoading(false)
      }
    }

    loadEmvByPlatform()
  }, [
    currentCustomerId,
    currentPeriod,
    customDateRange,
    selectedAccount?.account?.id,
    selectedAccount?.type,
    selectedPlatformIds,
    selectedPostType
  ])

  // Reset store data when component mounts
  useEffect(() => {
    reset()
  }, []) // Empty dependency array ensures this runs only once on mount

  // Update store when global customer changes
  useEffect(() => {
    if (selectedCustomer) {
      setCustomerId(selectedCustomer.id)
    } else {
      setCustomerId('')
    }
  }, [selectedCustomer, setCustomerId])

  // Reload data when account selection changes within the same customer
  useEffect(() => {
    if (currentCustomerId) {
      setActiveView('overview')
      // When returning to all-accounts view, restore the owned default for backward compat
      if (selectedAccount?.type !== 'individual') {
        setAccountType('owned')
      }
      loadDashboardAnalytics()
      loadTopPosts()
    }
  }, [selectedAccount, currentCustomerId, loadDashboardAnalytics, loadTopPosts])

  // Load permissions and Instagram connections using the logged-in user's org
  useEffect(() => {
    if (user?.organizationId) {
      loadPermissions(user.organizationId, true)
      loadConnections(user.organizationId)
    }
  }, [user?.organizationId, loadPermissions, loadConnections])

  // Define which filters are needed for each tab
  const tabFilterConfig: Record<
    string,
    { platform: boolean; postType: boolean; profile: boolean; period: boolean }
  > = {
    overview: { platform: true, postType: true, profile: true, period: true },
    posts: { platform: true, postType: true, profile: true, period: true },
    'hashtag-campaigns': { platform: true, postType: true, profile: true, period: true },
    'analytics-charts': { platform: true, postType: true, profile: true, period: true },
    'ai-intelligence': {
      platform: true,
      postType: true,
      profile: true,
      period: true
    },
    sponsors: { platform: true, postType: true, profile: false, period: true },
    profiles: { platform: true, postType: false, profile: false, period: true },
    'competitive-analysis': {
      platform: true,
      postType: false,
      profile: true,
      period: true
    },
    'brand-analytics': {
      platform: true,
      postType: true,
      profile: true,
      period: true
    },
    'content-performance': { platform: true, postType: false, profile: true, period: true },
    'brand-value': { platform: true, postType: false, profile: true, period: true }
  }

  // Helper function to convert platform name to platform ID
  const getPlatformId = (platformName: string) => {
    const platform = availablePlatforms.find((p) => p.name === platformName)
    return platform?.id
  }

  // Handle platform filter change
  const handlePlatformChange = (value: string) => {
    setSelectedPlatform(value)

    if (!currentCustomerId) return

    if (value === 'all') {
      setSelectedPlatformIds([])
    } else {
      const platformId = getPlatformId(value)
      if (platformId) {
        setSelectedPlatformIds([platformId])
      }
    }
  }

  // Handle post type filter change
  const handlePostTypeChange = (value: string) => {
    setLocalSelectedPostType(value)
    filterByPostType(value)
  }

  // Load available platforms on component mount
  useEffect(() => {
    loadAvailablePlatforms()
  }, [loadAvailablePlatforms])

  // Load post types only for tabs that need them
  useEffect(() => {
    if (tabFilterConfig[activeView]?.postType && currentCustomerId) {
      loadAvailablePostTypes()
    }
  }, [activeView, currentCustomerId, loadAvailablePostTypes])

  useEffect(() => {
    calculateAnalytics()
  }, [posts, calculateAnalytics])

  useEffect(() => {
    filterByPlatform(selectedPlatform)
  }, [selectedPlatform, filterByPlatform])

  // Calculate period-based growth percentages
  const calculateGrowthPercentage = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? '+∞%' : '0%'
    const percentage = ((current - previous) / previous) * 100
    const sign = percentage >= 0 ? '+' : ''
    const absPercentage = Math.abs(percentage)

    // Format with commas for thousands
    const formattedPercentage =
      absPercentage >= 1000
        ? absPercentage.toFixed(1).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        : absPercentage.toFixed(1)

    return `${sign}${formattedPercentage}%`
  }

  // Get stats based on dashboard analytics if available, otherwise fallback to local analytics
  const stats =
    dashboardAnalytics || analytics
      ? (() => {
          // Use dashboard analytics if available and customer is selected
          // The dashboardAnalytics will already be filtered by platform if selectedPlatformIds is set
          if (dashboardAnalytics && currentCustomerId) {
            const metrics = dashboardAnalytics.metrics
            const baseStats = [
              {
                label: 'Total Earned Media Value',
                value: metrics.totalEmv.current,
                change: metrics.totalEmv.change,
                positive: !metrics.totalEmv.change.startsWith('-'),
                icon: DollarSign,
                color: 'bg-green-500',
                category: 'hero'
              },
              {
                label: 'Average EMV',
                value: metrics.averageEmv.current,
                change: metrics.averageEmv.change,
                positive: !metrics.averageEmv.change.startsWith('-'),
                icon: TrendingUp,
                color: 'bg-purple-500',
                category: 'financial'
              },
              {
                label: 'Median EMV',
                value: metrics.medianEmv.current,
                change: metrics.medianEmv.change,
                positive: !metrics.medianEmv.change.startsWith('-'),
                icon: Target,
                color: 'bg-violet-500',
                category: 'financial'
              },
              {
                label: 'Sponsored EMV',
                value: metrics.sponsoredEmv.current,
                change: metrics.sponsoredEmv.change,
                positive: !metrics.sponsoredEmv.change.startsWith('-'),
                icon: Tag,
                color: 'bg-indigo-500',
                category: 'financial'
              },
              {
                label: 'Collaborator EMV',
                value: metrics.collaboratorEmv.current,
                change: metrics.collaboratorEmv.change,
                positive: !metrics.collaboratorEmv.change.startsWith('-'),
                icon: Users,
                color: 'bg-cyan-500',
                category: 'financial',
                tooltip: 'Collaborations expand your reach by exposing content to new audiences. Athletiverse recommends that at least 25% of your total EMV come from collaborator posts.',
                collaboratorPercent: (() => {
                  const collabVal = parseFloat(metrics.collaboratorEmv.current.replace(/[$,]/g, '')) || 0;
                  const totalVal = parseFloat(metrics.totalEmv.current.replace(/[$,]/g, '')) || 0;
                  return totalVal > 0 ? (collabVal / totalVal) * 100 : 0;
                })()
              },
              {
                label: 'Total Posts',
                value: metrics.totalPosts.current.toLocaleString(),
                change: metrics.totalPosts.change,
                positive: !metrics.totalPosts.change.startsWith('-'),
                icon: BarChart3,
                color: 'bg-blue-500',
                category: 'engagement'
              },
              {
                label: 'Total Views',
                value: metrics.totalViews.current.toLocaleString(),
                change: metrics.totalViews.change,
                positive: !metrics.totalViews.change.startsWith('-'),
                icon: Eye,
                color: 'bg-orange-500',
                category: 'engagement'
              },
              {
                label: 'Total Likes',
                value: metrics.totalLikes.current.toLocaleString(),
                change: metrics.totalLikes.change,
                positive: !metrics.totalLikes.change.startsWith('-'),
                icon: Heart,
                color: 'bg-red-500',
                category: 'engagement'
              }
            ]

            // Add media analysis cards if feature is enabled and data exists
            const hasVideoAnalysis = hasFeature('video_analysis')

            if (
              hasVideoAnalysis &&
              metrics.totalBrands &&
              metrics.totalDuration &&
              metrics.avgConfidence
            ) {
              baseStats.push(
                {
                  label: 'Total Brands',
                  value: metrics.totalBrands.current.toLocaleString(),
                  change: metrics.totalBrands.change,
                  positive: !metrics.totalBrands.change.startsWith('-'),
                  icon: Zap,
                  color: 'bg-yellow-500',
                  category: 'brand-analysis'
                },
                {
                  label: 'Media Duration',
                  value: metrics.totalDuration.current,
                  change: metrics.totalDuration.change,
                  positive: !metrics.totalDuration.change.startsWith('-'),
                  icon: Clock,
                  color: 'bg-cyan-500',
                  category: 'brand-analysis'
                },
                {
                  label: 'Avg Analysis Confidence',
                  value: `${Math.round(metrics.avgConfidence.current * 100)}%`,
                  change: metrics.avgConfidence.change,
                  positive: !metrics.avgConfidence.change.startsWith('-'),
                  icon: Target,
                  color: 'bg-teal-500',
                  category: 'brand-analysis'
                }
              )
            }

            return baseStats
          }

          // Fallback to original analytics calculation
          if (!analytics) return []
          let sourceData = analytics.combined

          // If a specific profile is selected, use that profile's data
          if (selectedProfile) {
            const profile = profiles.find((p) => p.username === selectedProfile)
            if (profile) {
              sourceData = {
                totalPosts: profile.totalPosts,
                totalEMV: profile.totalEMV,
                averageEMV: profile.averageEMV,
                totalViews: profile.totalViews,
                totalLikes: profile.totalLikes,
                totalComments: profile.totalComments,
                totalShares: profile.totalShares,
                sponsoredPosts: profile.sponsoredPosts,
                sponsoredEMV: profile.sponsoredEMV
              }
            }
          }

          // Calculate historical comparison (for now, using simplified mock data)
          // In a real implementation, you'd fetch historical data from API
          const historicalData = {
            totalPosts: Math.floor(sourceData.totalPosts * 0.85),
            totalEMV: sourceData.totalEMV * 0.92,
            averageEMV: sourceData.averageEMV * 0.96,
            totalViews: Math.floor(sourceData.totalViews * 0.88),
            totalLikes: Math.floor(sourceData.totalLikes * 0.89),
            sponsoredEMV: sourceData.sponsoredEMV * 0.83
          }

          return [
            {
              label: 'Total Posts',
              value: sourceData.totalPosts.toLocaleString(),
              change: calculateGrowthPercentage(
                sourceData.totalPosts,
                historicalData.totalPosts
              ),
              positive: sourceData.totalPosts >= historicalData.totalPosts,
              icon: BarChart3,
              color: 'bg-blue-500'
            },
            {
              label: 'Total Earned Media Value',
              value: formatEMV(sourceData.totalEMV),
              change: calculateGrowthPercentage(
                sourceData.totalEMV,
                historicalData.totalEMV
              ),
              positive: sourceData.totalEMV >= historicalData.totalEMV,
              icon: DollarSign,
              color: 'bg-green-500'
            },
            {
              label: 'Average Earned Media Value',
              value: formatEMV(sourceData.averageEMV),
              change: calculateGrowthPercentage(
                sourceData.averageEMV,
                historicalData.averageEMV
              ),
              positive: sourceData.averageEMV >= historicalData.averageEMV,
              icon: TrendingUp,
              color: 'bg-purple-500'
            },
            {
              label: 'Total Views',
              value: sourceData.totalViews.toLocaleString(),
              change: calculateGrowthPercentage(
                sourceData.totalViews,
                historicalData.totalViews
              ),
              positive: sourceData.totalViews >= historicalData.totalViews,
              icon: Eye,
              color: 'bg-orange-500'
            },
            {
              label: 'Total Likes',
              value: sourceData.totalLikes.toLocaleString(),
              change: calculateGrowthPercentage(
                sourceData.totalLikes,
                historicalData.totalLikes
              ),
              positive: sourceData.totalLikes >= historicalData.totalLikes,
              icon: Heart,
              color: 'bg-red-500'
            },
            {
              label: 'Sponsored Earned Media Value',
              value: formatEMV(sourceData.sponsoredEMV),
              change: calculateGrowthPercentage(
                sourceData.sponsoredEMV,
                historicalData.sponsoredEMV
              ),
              positive: sourceData.sponsoredEMV >= historicalData.sponsoredEMV,
              icon: Tag,
              color: 'bg-indigo-500'
            }
          ]
        })()
      : []

  // Show message if no customer is selected
  if (!selectedCustomer) {
    // For regular users, show loading state instead of "No Customer Selected" during initial load
    if (isRegularUser(user) && loadingCustomers) {
      return (
        <div className='flex items-center justify-center h-64'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4'></div>
            <p className='text-gray-500'>Loading your analytics...</p>
          </div>
        </div>
      )
    }

    // For admin/support users or when not loading, show the selection prompt
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <Building2 className='h-12 w-12 text-gray-400 mx-auto mb-4' />
          <h3 className='text-lg font-medium text-gray-900 mb-2'>
            No Customer Selected
          </h3>
          <p className='text-gray-500'>
            Please select a customer from the sidebar to view analytics
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-6'>

      {/* One-time announcement of the Shared Reports feature */}
      <SharedReportsBanner />

      {/* Header */}
      <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0 pb-4 border-b border-gray-200'>
        <div>
          <h1 className='text-xl font-bold text-gray-900 tracking-tight'>
            {selectedCustomer.customerName}
          </h1>
          {/* Account and Platform Badges */}
          {selectedAccount && (
            <div className='flex items-center space-x-1.5 mt-2'>
              {selectedAccount.type === 'individual' &&
              selectedAccount.account ? (
                <>
                  <span className='inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded border border-blue-200'>
                    {selectedAccount.account.displayName ||
                      selectedAccount.account.username}
                  </span>
                  <span className='inline-flex items-center px-2 py-0.5 bg-purple-50 text-purple-700 text-xs font-medium rounded border border-purple-200'>
                    {selectedAccount.account.platformDisplayName}
                  </span>
                  {isInstagramGraphApiEnabled() && hasFeature('instagram_graph_api') &&
                    selectedAccount.account.platformDisplayName?.toLowerCase() === 'instagram' &&
                    connections.some(c => c.linkedPlatformAccountId === selectedAccount.accountId && c.tokenStatus === 'active') && (
                    <span className='inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded border border-green-200'>
                      <Zap className='h-3 w-3' />
                      Enhanced API
                    </span>
                  )}
                </>
              ) : (
                <span className='inline-flex items-center px-2 py-0.5 bg-gray-50 text-gray-600 text-xs font-medium rounded border border-gray-200'>
                  All Accounts
                </span>
              )}
            </div>
          )}
        </div>

        <div className='flex items-center space-x-2'>
          {/* Account Type dropdown — disabled when a specific account is selected */}
          {tabFilterConfig[activeView]?.profile && (
            <select
              value={accountType}
              onChange={(e) => setAccountType(e.target.value as 'all' | 'owned' | 'partner')}
              disabled={!currentCustomerId || selectedAccount?.type === 'individual'}
              title={selectedAccount?.type === 'individual' ? 'Account type filter applies to aggregate views only' : undefined}
              className='px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors shadow-sm'
            >
              <option value="all">All Accounts</option>
              <option value="owned">Owned</option>
              <option value="partner">Partner / Influencer</option>
            </select>
          )}

          {/* Show platform filter only for tabs that need it */}
          {tabFilterConfig[activeView]?.platform && (
            <select
              value={selectedPlatform}
              onChange={(e) => handlePlatformChange(e.target.value)}
              disabled={!currentCustomerId}
              className='px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors shadow-sm'
            >
              <option value='all'>All Platforms</option>
              {availablePlatforms.map((platform) => (
                <option key={platform.id} value={platform.name}>
                  {platform.displayName}
                </option>
              ))}
            </select>
          )}

          {/* Show post type filter only for tabs that need it */}
          {tabFilterConfig[activeView]?.postType && (
            <select
              value={localSelectedPostType}
              onChange={(e) => handlePostTypeChange(e.target.value)}
              disabled={!currentCustomerId}
              className='px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors shadow-sm'
            >
              <option value='all'>All Post Types</option>
              {availablePostTypes.map((postType: any) => (
                <option key={postType.id} value={postType.typeName}>
                  {postType.displayName}
                </option>
              ))}
            </select>
          )}


          {/* Show period filter only for tabs that need it */}
          {tabFilterConfig[activeView]?.period && (
            <DateRangePicker
              value={customDateRange}
              onChange={(range, period) => {
                if (period === 'custom' && range) {
                  setCustomDateRange(range)
                } else {
                  setPeriod(period as any)
                  setCustomDateRange(null)
                }
              }}
              maxRetentionDays={
                permissions?.effectiveLimits?.post_retention_days || 90
              }
              disabled={!currentCustomerId}
            />
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className='px-2 pt-2 pb-0'>
        <div className='flex items-center bg-gradient-to-r from-gray-100 to-gray-200 p-1 rounded-lg shadow-inner'>
          <div className='flex space-x-1 flex-1'>
          {[
            {
              id: 'overview',
              label: 'Overview',
              icon: BarChart3,
              requiresCustomer: false
            },
            {
              id: 'content-performance',
              label: 'Value Map',
              icon: LayoutGrid,
              requiresCustomer: true
            },
            {
              id: 'brand-value',
              label: 'Brand Value',
              icon: Tag,
              requiresCustomer: true
            },
            {
              id: 'posts',
              label: 'Posts',
              icon: MessageCircle,
              requiresCustomer: true
            },
            // Hashtag Campaigns - to be determined whether to remove this page and functionality entirely
            // {
            //   id: 'hashtag-campaigns',
            //   label: 'Hashtag Campaigns',
            //   icon: Tag,
            //   requiresCustomer: true
            // },
            {
              id: 'analytics-charts',
              label: 'Analytics Charts',
              icon: PieChart,
              requiresCustomer: true
            },
            // AI Intelligence Center - feature to be further considered in the future
            // {
            //   id: 'ai-intelligence',
            //   label: 'AI Intelligence Center',
            //   icon: Sparkles,
            //   requiresCustomer: true
            // },
            // Brand Discovery — hidden again (see the import comment above).
            // {
            //   id: 'brand-analytics',
            //   label: 'Brand Discovery',
            //   icon: Zap,
            //   requiresCustomer: true
            // },
            {
              id: 'competitive-analysis',
              label: 'Competitor Benchmarking',
              icon: GitCompare,
              requiresCustomer: true
            }
          ].map((tab) => {
            const isDisabled = tab.requiresCustomer && !currentCustomerId
            return (
              <button
                key={tab.id}
                onClick={() => {
                  if (!isDisabled) {
                    // Clear account filters when leaving competitive analysis page
                    if (
                      activeView === 'competitive-analysis' &&
                      tab.id !== 'competitive-analysis'
                    ) {
                      setSelectedAccountIds([])
                      // Reload data for the overview page with cleared filters
                      if (tab.id === 'overview') {
                        loadDashboardAnalytics()
                        loadTopPosts()
                      }
                    }
                    setActiveView(tab.id as any)
                  }
                }}
                disabled={isDisabled}
                className={`flex items-center px-4 py-2 rounded-md text-xs font-semibold transition-all ${
                  isDisabled
                    ? 'text-gray-400 cursor-not-allowed opacity-50'
                    : activeView === tab.id
                    ? 'bg-white text-blue-600 shadow-md border border-white/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                <tab.icon className='h-3.5 w-3.5 mr-1.5' />
                {tab.label}
              </button>
            )
          })}
          </div>
          {activeView === 'overview' && (
            <button
              onClick={handleRefresh}
              disabled={isRefreshing || !currentCustomerId}
              className='p-1.5 text-gray-500 hover:text-gray-700 hover:bg-white/60 rounded-md transition-colors disabled:opacity-40 ml-1'
              title='Refresh'
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {/* One-time announcement of the new Value Map + Brand Value tabs */}
      <NewFeaturesAnnouncement />

      {/* Content */}
      <AnimatePresence mode='wait'>
        <motion.div
          key={activeView}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
        >
          {activeView === 'overview' &&
            (error ? (
              <div className='flex items-center justify-center min-h-[400px]'>
                <div className='text-center max-w-md'>
                  <div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4'>
                    <svg
                      className='h-6 w-6 text-red-600'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                      />
                    </svg>
                  </div>
                  <h3 className='text-lg font-medium text-gray-900 mb-2'>
                    Unable to Load Analytics
                  </h3>
                  <p className='text-sm text-gray-600 mb-4'>{error}</p>
                  <Button
                    onClick={() => loadDashboardAnalytics()}
                    className='bg-avi-primary hover:bg-avi-primary/90'
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            ) : loading || !dashboardAnalytics ? (
              <div className='flex items-center justify-center min-h-[400px]'>
                <div className='text-center'>
                  <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-avi-primary mx-auto mb-4'></div>
                  <p className='text-gray-600'>Loading analytics data...</p>
                </div>
              </div>
            ) : (
              <div className='space-y-4'>
                {/* Hero EMV Card */}
                <Card className='relative bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600 border-0 overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300'>
                  {/* Decorative background elements */}
                  <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE4YzAtMy4zMTQgMi42ODYtNiA2LTZzNiAyLjY4NiA2IDYtMi42ODYgNi02IDYtNi0yLjY4Ni02LTZ6TTAgMThjMC0zLjMxNCAyLjY4Ni02IDYtNnM2IDIuNjg2IDYgNi0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNnptMzYgMzZjMC0zLjMxNCAyLjY4Ni02IDYtNnM2IDIuNjg2IDYgNi0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>

                  <div className='relative p-4'>
                    <div className='flex items-start justify-between'>
                      <div className='flex-1'>
                        <h2 className='text-xs font-semibold text-green-50 mb-1 tracking-wide uppercase'>
                          Total Earned Media Value
                        </h2>
                        <div className='flex items-baseline'>
                          <p className='text-3xl font-bold text-white tracking-tight'>
                            {stats.find(
                              (s) => s.label === 'Total Earned Media Value'
                            )?.value || '$0'}
                          </p>
                          <div className='ml-3 flex items-center bg-white/20 backdrop-blur-sm rounded-full px-2 py-0.5'>
                            <span
                              className={`text-sm font-bold ${
                                stats.find(
                                  (s) => s.label === 'Total Earned Media Value'
                                )?.positive
                                  ? 'text-green-50'
                                  : 'text-red-100'
                              }`}
                            >
                              {stats.find(
                                (s) => s.label === 'Total Earned Media Value'
                              )?.change || '0%'}
                            </span>
                            <span className='text-xs text-green-100 ml-1'>
                              vs last period
                            </span>
                          </div>
                        </div>
                        {/* Mini EMV Trend Chart */}
                        <div className='mt-2 h-12 flex items-end space-x-1'>
                          {emvTrendLoading ? (
                            <div className='w-full flex items-center justify-center'>
                              <div className='text-xs text-gray-500'>
                                Loading trend data...
                              </div>
                            </div>
                          ) : emvTrendData.length > 0 ? (
                            (() => {
                              // Calculate max value for scaling
                              const maxValue = Math.max(
                                ...emvTrendData.map((d) => d.value)
                              )
                              const minValue = Math.min(
                                ...emvTrendData.map((d) => d.value)
                              )
                              const range = maxValue - minValue

                              // For better visualization, use a minimum height percentage
                              const getHeight = (value: number) => {
                                if (range === 0) return 50 // All values same
                                const normalized = (value - minValue) / range
                                return Math.max(20, normalized * 100) // Min 20% height
                              }

                              return emvTrendData.map((day, i) => (
                                <div
                                  key={i}
                                  className='flex-1 bg-white/40 rounded-t hover:bg-white/60 transition-all duration-200 cursor-pointer'
                                  style={{ height: `${getHeight(day.value)}%` }}
                                  title={`${
                                    day.date
                                  }: $${day.value.toLocaleString()}`}
                                />
                              ))
                            })()
                          ) : (
                            // Fallback to placeholder bars if no data
                            [
                              40, 55, 45, 60, 50, 65, 70, 85, 75, 90, 80, 95
                            ].map((height, i) => (
                              <div
                                key={i}
                                className='flex-1 bg-white/40 rounded-t'
                                style={{ height: `${height}%` }}
                              />
                            ))
                          )}
                        </div>
                      </div>
                      <div className='p-2 bg-white/20 backdrop-blur-sm rounded-xl shadow-lg'>
                        <DollarSign className='h-8 w-8 text-white' />
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Grouped Metric Cards */}
                <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
                  {/* Financial Metrics Group */}
                  <Card className='p-4 bg-gradient-to-br from-white to-purple-50/30 border-purple-100 hover:shadow-lg transition-all duration-300 group'>
                    <div className='flex items-center justify-between mb-3'>
                      <h3 className='text-xs font-semibold text-gray-600 uppercase tracking-wider'>
                        Financial Metrics
                      </h3>
                      <div className='h-7 w-7 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300'>
                        <DollarSign className='h-3.5 w-3.5 text-white' />
                      </div>
                    </div>
                    <div className='space-y-2'>
                      {stats.filter((s: any) => s.category === 'financial')
                        .length > 0 ? (
                        stats
                          .filter((s: any) => s.category === 'financial')
                          .map((stat: any, index: number) => (
                            <div
                              key={index}
                              className='flex items-center justify-between p-2 rounded-lg hover:bg-white/80 transition-colors duration-200'
                            >
                              <div className='flex items-center space-x-2'>
                                <div
                                  className={`p-1.5 rounded-lg ${stat.color} shadow-sm`}
                                >
                                  <stat.icon className='h-3 w-3 text-white' />
                                </div>
                                <div>
                                  <div className='flex items-center gap-1'>
                                    <p className='text-sm font-semibold text-gray-900'>
                                      {stat.value}
                                    </p>
                                    {stat.collaboratorPercent !== undefined && stat.collaboratorPercent < 25 && (
                                      <Tooltip
                                        content={`Collaborator EMV is ${stat.collaboratorPercent.toFixed(1)}% of total — below the recommended 25%.`}
                                        placement="top"
                                        maxWidth={220}
                                      >
                                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500 cursor-help" />
                                      </Tooltip>
                                    )}
                                  </div>
                                  <span className='text-xs text-gray-600 flex items-center gap-1'>
                                    {stat.label}
                                    {stat.tooltip && (
                                      <Tooltip content={stat.tooltip} placement="top" maxWidth={250}>
                                        <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                                      </Tooltip>
                                    )}
                                  </span>
                                </div>
                              </div>
                              <span
                                className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                                  stat.positive
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                                }`}
                              >
                                {stat.change}
                              </span>
                            </div>
                          ))
                      ) : (
                        <div className='flex items-center justify-center py-6'>
                          <div className='text-center'>
                            <DollarSign className='h-6 w-6 text-gray-300 mx-auto mb-2' />
                            <p className='text-xs text-gray-500'>
                              No financial data available
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>

                  {/* Engagement Metrics Group */}
                  <Card className='p-4 bg-gradient-to-br from-white to-blue-50/30 border-blue-100 hover:shadow-lg transition-all duration-300 group'>
                    <div className='flex items-center justify-between mb-3'>
                      <h3 className='text-xs font-semibold text-gray-600 uppercase tracking-wider'>
                        Engagement Metrics
                      </h3>
                      <div className='h-7 w-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300'>
                        <Heart className='h-3.5 w-3.5 text-white' />
                      </div>
                    </div>
                    <div className='space-y-2'>
                      {stats.filter((s: any) => s.category === 'engagement')
                        .length > 0 ? (
                        stats
                          .filter((s: any) => s.category === 'engagement')
                          .map((stat, index) => (
                            <div
                              key={index}
                              className='flex items-center justify-between p-2 rounded-lg hover:bg-white/80 transition-colors duration-200'
                            >
                              <div className='flex items-center space-x-2'>
                                <div
                                  className={`p-1.5 rounded-lg ${stat.color} shadow-sm`}
                                >
                                  <stat.icon className='h-3 w-3 text-white' />
                                </div>
                                <div>
                                  <p className='text-sm font-semibold text-gray-900'>
                                    {stat.value}
                                  </p>
                                  <p className='text-xs text-gray-600'>
                                    {stat.label}
                                  </p>
                                </div>
                              </div>
                              <span
                                className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                                  stat.positive
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                                }`}
                              >
                                {stat.change}
                              </span>
                            </div>
                          ))
                      ) : (
                        <div className='flex items-center justify-center py-6'>
                          <div className='text-center'>
                            <BarChart3 className='h-8 w-8 text-gray-300 mx-auto mb-2' />
                            <p className='text-sm text-gray-500'>
                              No engagement data available
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Mini Posting Heatmap - only show if we have engagement data */}
                    {stats.filter((s: any) => s.category === 'engagement')
                      .length > 0 && (
                      <div className='mt-3 pt-3 border-t'>
                        <p className='text-xs font-medium text-gray-500 mb-1.5'>
                          Posting Activity
                        </p>
                        {heatmapLoading ? (
                          <div className='flex items-center justify-center py-2'>
                            <div className='text-xs text-gray-400'>
                              Loading...
                            </div>
                          </div>
                        ) : heatmapData && heatmapData.weeklyBreakdown ? (
                          <div className='grid grid-cols-7 gap-1'>
                            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(
                              (dayLabel, dayIndex) => {
                                // Get data for this day across all weeks
                                const weeksToShow = Math.min(
                                  4,
                                  heatmapData.weeklyBreakdown.length
                                )
                                const weeksData =
                                  heatmapData.weeklyBreakdown.slice(
                                    0,
                                    weeksToShow
                                  )

                                return (
                                  <div key={dayIndex} className='text-center'>
                                    <div className='text-xs text-gray-400 mb-0.5'>
                                      {dayLabel}
                                    </div>
                                    <div
                                      className='grid gap-0.5'
                                      style={{
                                        gridTemplateRows: `repeat(${weeksToShow}, minmax(0, 1fr))`
                                      }}
                                    >
                                      {weeksData.map(
                                        (week: any, weekIndex: number) => {
                                          const dayData =
                                            week.dayOfWeek[dayIndex.toString()]
                                          const intensity =
                                            dayData?.intensity || 0
                                          const postCount =
                                            dayData?.postCount || 0

                                          return (
                                            <div
                                              key={weekIndex}
                                              className='w-full h-1.5 rounded-sm transition-opacity hover:opacity-80'
                                              style={{
                                                backgroundColor:
                                                  intensity > 0
                                                    ? `rgba(99, 102, 241, ${intensity})`
                                                    : 'rgba(229, 231, 235, 1)' // gray-200
                                              }}
                                              title={`Week ${
                                                week.weekNumber
                                              }: ${postCount} post${
                                                postCount !== 1 ? 's' : ''
                                              }`}
                                            />
                                          )
                                        }
                                      )}
                                    </div>
                                  </div>
                                )
                              }
                            )}
                          </div>
                        ) : (
                          <div className='flex items-center justify-center py-2'>
                            <div className='text-xs text-gray-400'>
                              No posting activity data
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>

                  {/* Video Analysis Metrics Group */}
                  <Card className='p-4 bg-gradient-to-br from-white to-yellow-50/30 border-yellow-100 hover:shadow-lg transition-all duration-300 group'>
                    <div className='flex items-center justify-between mb-3'>
                      <h3 className='text-xs font-semibold text-gray-600 uppercase tracking-wider'>
                        Brand Discovery
                      </h3>
                      <div className='h-7 w-7 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300'>
                        <Zap className='h-3.5 w-3.5 text-white' />
                      </div>
                    </div>
                    {hasFeature('video_analysis') &&
                    stats.filter((s: any) => s.category === 'brand-analysis')
                      .length > 0 ? (
                      <div className='space-y-2'>
                        {stats
                          .filter((s: any) => s.category === 'brand-analysis')
                          .map((stat, index) => (
                            <div
                              key={index}
                              className='flex items-center justify-between p-2 rounded-lg hover:bg-white/80 transition-colors duration-200'
                            >
                              <div className='flex items-center space-x-2'>
                                <div
                                  className={`p-1.5 rounded-lg ${stat.color} shadow-sm`}
                                >
                                  <stat.icon className='h-3 w-3 text-white' />
                                </div>
                                <div>
                                  <p className='text-sm font-semibold text-gray-900'>
                                    {stat.value}
                                  </p>
                                  <p className='text-xs text-gray-600'>
                                    {stat.label}
                                  </p>
                                </div>
                              </div>
                              <span
                                className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                                  stat.positive
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                                }`}
                              >
                                {stat.change}
                              </span>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className='flex flex-col items-center justify-center py-6 text-center'>
                        <div className='p-3 bg-gray-100 rounded-full mb-3'>
                          <Lock className='h-6 w-6 text-gray-400' />
                        </div>
                        <h4 className='text-sm font-semibold text-gray-900 mb-1'>
                          Upgrade to Access Brand Analytics
                        </h4>
                        <p className='text-xs text-gray-600 mb-3'>
                          Unlock powerful brand detection and analysis
                          capabilities
                        </p>
                        <button className='text-xs text-indigo-600 hover:text-indigo-700 font-medium'>
                          Learn More
                        </button>
                      </div>
                    )}
                  </Card>
                </div>

                {/* EMV and Posts by Platform Charts */}
                <EMVByPlatformCharts
                  data={emvByPlatformData}
                  loading={emvByPlatformLoading}
                />
              </div>
            ))}

          {activeView === 'posts' && <PostsTable />}

          {/* Hashtag Campaigns - to be determined whether to remove this page and functionality entirely */}
          {/* {activeView === 'hashtag-campaigns' && <HashtagAnalyticsPage />} */}

          {activeView === 'analytics-charts' && <EMVCharts />}

          {/* AI Intelligence Center - feature to be further considered in the future */}
          {/* {activeView === 'ai-intelligence' && <AIIntelligenceCenter />} */}

          {activeView === 'competitive-analysis' && <CompetitiveAnalysis />}

          {activeView === 'content-performance' && <ContentPerformancePage />}

          {activeView === 'brand-value' && <BrandValuePanel />}

          {/* Brand Discovery — hidden again (see the import comment above). */}
          {/* {activeView === 'brand-analytics' && <BrandAnalytics />} */}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

export default SocialMediaDashboard
